/**
 * DEV-259 US-1 — portão de acesso do formulário da pessoa gestora
 * (Figma 20080:1214 e variações). Sem header, sem Keycloak: entra com o token
 * do link; o BFF devolve a sessão que as etapas usam.
 */
import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import {
  Button,
  FormField,
  FormFieldControl,
  FormFieldError,
  FormFieldLabel,
  Input,
  Skeleton,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@eureca/ui';
import { AlignmentApiError, alignmentApi } from '../shared/api';
import { firstPageName } from '../shared/definition';
import { ONBOARDING, TOAST } from '../shared/messages';
import { saveAlignmentSession } from '../shared/session';
import { GraveErrorScreen, NoConnectionScreen } from '../shared/SystemScreens';
import illustrationUrl from './onboarding-illustration.svg';

const emailSchema = z.email();
const NON_CRITICAL_TOAST_MS = 6000;

export function alignmentAccessQueryKey(tenant: string, formId: string) {
  return ['alignment-access', tenant, formId] as const;
}

function nonCriticalToast(message: string) {
  toast(message, {
    duration: NON_CRITICAL_TOAST_MS,
    action: { label: TOAST.close, onClick: () => undefined },
  });
}

function OnboardingLayout({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh grid-cols-2 bg-background" data-testid="onboarding-page">
      <div className="relative overflow-hidden bg-secondary">
        <img src={illustrationUrl} alt="" className="absolute inset-0 size-full object-cover" />
      </div>
      <div className="flex items-center px-32">
        <div className="flex w-full max-w-[417px] flex-col gap-8">{children}</div>
      </div>
    </main>
  );
}

export function OnboardingPage({ tenant, formId }: { tenant: string; formId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tokenRef = useRef<HTMLInputElement>(null);
  // AC-08: `isPending` só muda no próximo render; o ref barra o 2º clique na hora.
  const inFlight = useRef(false);

  const access = useQuery({
    queryKey: alignmentAccessQueryKey(tenant, formId),
    queryFn: ({ signal }) => alignmentApi.getAccess(tenant, formId, signal),
    // Sem retentativa automática: as telas de erro têm "Tentar novamente".
    retry: false,
    staleTime: Infinity,
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [tokenRejected, setTokenRejected] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [blockedClick, setBlockedClick] = useState(false);
  const [fatal, setFatal] = useState(false);

  const emailValid = emailSchema.safeParse(email.trim()).success;
  const anyEmpty = !name.trim() || !email.trim() || !token.trim();
  const canStart = !anyEmpty && emailValid && !tokenRejected;
  const blockMessage = anyEmpty ? ONBOARDING.emptyFields : ONBOARDING.invalidFields;

  const start = useMutation({
    mutationFn: async () => {
      const opened = await alignmentApi.openSession(tenant, formId, {
        token: token.trim(),
        name: name.trim(),
        email: email.trim(),
      });
      saveAlignmentSession(formId, opened);
      const definition = await queryClient.fetchQuery({
        queryKey: ['alignment-definition', opened.jobDescriptionId],
        queryFn: ({ signal }) => alignmentApi.getDefinition(opened.sessionToken, signal),
      });
      return firstPageName(definition);
    },
    onSuccess: (pageName) => {
      if (!pageName) {
        setFatal(true);
        return;
      }
      void navigate({
        to: '/descricao-vaga/$tenant/$formId/etapa/$pageName',
        params: { tenant, formId, pageName },
      });
    },
    onSettled: () => {
      inFlight.current = false;
    },
    onError: (err) => {
      if (!(err instanceof AlignmentApiError)) {
        nonCriticalToast(TOAST.server);
        return;
      }
      if (err.code === 'invalid_token') setTokenRejected(true);
      else if (err.status === 429) nonCriticalToast(TOAST.tooManyAttempts);
      else if (err.status === 0) nonCriticalToast(TOAST.noConnection);
      else if (err.code === 'link_not_found' || err.code === 'alignment_unconfigured')
        setFatal(true);
      else nonCriticalToast(TOAST.server);
    },
  });

  function handleStart() {
    if (inFlight.current || start.isPending) return;
    if (!canStart) {
      setBlockedClick(true);
      return;
    }
    inFlight.current = true;
    start.mutate();
  }

  async function handlePaste() {
    try {
      const text = await navigator.clipboard.readText();
      setToken(text.trim());
      setTokenRejected(false);
    } catch {
      tokenRef.current?.focus();
    }
  }

  if (fatal) {
    return (
      <GraveErrorScreen
        onRetry={() => {
          setFatal(false);
          void access.refetch();
        }}
      />
    );
  }
  if (access.isError) {
    const offline = access.error instanceof AlignmentApiError && access.error.status === 0;
    const retry = () => void access.refetch();
    return offline ? <NoConnectionScreen onRetry={retry} /> : <GraveErrorScreen onRetry={retry} />;
  }

  const showEmailError = emailTouched && email.trim() !== '' && !emailValid;

  return (
    <OnboardingLayout>
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium leading-6 text-foreground">{ONBOARDING.title}</h1>
        {access.data ? (
          <p className="text-base leading-6 text-muted-foreground">
            {ONBOARDING.subtitleBefore}
            <span className="font-medium">{access.data.programName}</span>
            {ONBOARDING.subtitleAfter}
          </p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="onboarding-loading">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
          </div>
        )}
      </div>

      <form
        noValidate
        className="flex flex-col gap-8"
        onSubmit={(e) => {
          e.preventDefault();
          handleStart();
        }}
      >
        <div className="flex flex-col gap-5">
          <FormField errorTone="neutral">
            <FormFieldLabel>{ONBOARDING.nameLabel}</FormFieldLabel>
            <FormFieldControl>
              {(props) => (
                <Input
                  {...props}
                  autoComplete="name"
                  maxLength={120}
                  placeholder={ONBOARDING.namePlaceholder}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="onboarding-name"
                />
              )}
            </FormFieldControl>
          </FormField>

          <FormField error={showEmailError} errorTone="neutral">
            <FormFieldLabel>{ONBOARDING.emailLabel}</FormFieldLabel>
            <FormFieldControl>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  variant={showEmailError ? 'destructive' : 'default'}
                  placeholder={ONBOARDING.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  data-testid="onboarding-email"
                />
              )}
            </FormFieldControl>
            <FormFieldError>{ONBOARDING.invalidEmail}</FormFieldError>
          </FormField>

          <FormField error={tokenRejected} errorTone="neutral">
            <FormFieldLabel>{ONBOARDING.tokenLabel}</FormFieldLabel>
            <div
              className={cn(
                'flex h-9 items-center gap-2 rounded-md border bg-background pr-1 shadow-2xs focus-within:ring-2',
                tokenRejected
                  ? 'border-destructive focus-within:ring-destructive/40'
                  : 'border-input focus-within:ring-primary/40'
              )}
            >
              <FormFieldControl>
                {(props) => (
                  <input
                    {...props}
                    ref={tokenRef}
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={200}
                    placeholder={ONBOARDING.tokenPlaceholder}
                    value={token}
                    onChange={(e) => {
                      setToken(e.target.value);
                      setTokenRejected(false);
                    }}
                    className="h-full min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
                    data-testid="onboarding-token"
                  />
                )}
              </FormFieldControl>
              <Button
                type="button"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => void handlePaste()}
                data-testid="onboarding-token-paste"
              >
                {ONBOARDING.paste}
              </Button>
            </div>
            <FormFieldError>{ONBOARDING.invalidToken}</FormFieldError>
          </FormField>
        </div>

        <TooltipProvider delayDuration={0}>
          <Tooltip
            open={!canStart && !start.isPending && (hovering || blockedClick)}
            onOpenChange={(open) => {
              setHovering(open);
              if (!open) setBlockedClick(false);
            }}
          >
            <TooltipTrigger asChild>
              <Button
                type="submit"
                size="lg"
                className={cn('self-start px-8', !canStart && 'cursor-not-allowed opacity-50')}
                aria-disabled={!canStart}
                loading={start.isPending}
                data-testid="onboarding-start"
              >
                {ONBOARDING.start}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" variant="muted">
              {blockMessage}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </form>
    </OnboardingLayout>
  );
}
